import {
  ANNOTATION_RESOURCE_TYPE,
  ANNOTATION_TYPE,
  OWNER_TYPE,
} from "@modules/annotation/service/annotation.constant";
import { parseOwningComponentIdFromAnchor } from "@modules/annotation/service/annotation.anchor.utils";
import type {
  AnnotationContext,
  ImageAnnotationRequest,
  ImageAnnotationDataModel,
} from "@modules/annotation/types";

type RequestContext = Partial<
  Pick<
    AnnotationContext,
    | "resourceUuid"
    | "resourceType"
    | "contextType"
    | "userId"
    | "pageId"
    | "componentId"
    | "academicYear"
  >
>;

export interface ImageAnnotationRequestParams {
  saveResult: ImageAnnotationDataModel;
  /** Injected annotation context (may be undefined when not provided). */
  annotationContext?: RequestContext;
  /** Full media anchor, i.e. `<parentAnchor>_<index>`. */
  anchor: string;
  /** Explicit resource id (e.g. from the parent rich text), takes priority over the context. */
  annotationResourceId?: string | number;
  annotationResourceType?: string;
  ownerType?: string;
  /** Fallback owner id when the context has no user id. */
  fallbackOwnerId?: number | string;
  title?: string;
  mediaUuid?: string;
  indexPosition?: number;
  creatorName?: string;
  createdBy?: number | string;
}

/**
 * Resolve the id of the resource owning the annotation.
 * Same order as the legacy image annotation: explicit id -> context component id -> anchor.
 */
export function resolveAnnotationResourceId(
  explicitId: string | number | undefined,
  context: RequestContext | undefined,
  anchor: string,
): number {
  const explicit = Number(explicitId);
  if (explicitId !== undefined && explicitId !== "" && Number.isFinite(explicit) && explicit > 0) {
    return explicit;
  }

  const contextComponentId = Number(context?.componentId);
  if (Number.isFinite(contextComponentId) && contextComponentId > 0) {
    return contextComponentId;
  }

  return parseOwningComponentIdFromAnchor(anchor);
}

/**
 * Build the request data of a new image annotation.
 * Mirrors the legacy `createImageAnnotation` fields. The grouped canvas data
 * (`payload`, `boundingBox`) replaces the legacy `data.highlighterSelectors`.
 */
export function buildImageAnnotationRequest(
  params: ImageAnnotationRequestParams,
): ImageAnnotationRequest {
  const { title, saveResult, annotationContext: context, anchor } = params;
  const ownerId = context?.userId ?? params.fallbackOwnerId;

  return {
    resourceUuid: context?.resourceUuid,
    resourceType: context?.resourceType,
    academicYear: context?.academicYear ?? 0,
    annotationResourceId: resolveAnnotationResourceId(params.annotationResourceId, context, anchor),
    annotationResourceType:
      params.annotationResourceType || ANNOTATION_RESOURCE_TYPE.LESSON_COMPONENT,
    anchor,
    contextType: context?.contextType,
    groupingId: context?.pageId,
    type: ANNOTATION_TYPE.IMAGE_ANNOTATION,
    data: saveResult,
    collectionOwner: context?.userId,
    allowComment: true,
    ownerType: params.ownerType || OWNER_TYPE.USER,
    ownerId,
    indexPosition: params.indexPosition ?? 0,
    visibleByStudents: false,
    title: title ?? "",
  };
}

/**
 * Character offset of the image inside its rich text, used to sort annotations by position.
 * Returns 0 when the image is not inside an annotatable rich text.
 */
export function getImageIndexPosition(fromElement: Element | null, index: string): number {
  const richText = fromElement?.closest(".annotatable-richtext");
  if (!richText) return 0;

  const imageElement = richText.querySelector(`.media.image [index='${index}']`);
  if (!imageElement) return 0;

  const range = document.createRange();
  range.selectNodeContents(richText);
  range.setEnd(imageElement, 0);
  return range.toString().length;
}
